import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Tooltip
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";

import Card from "../../Components/Card";
import ResourceTable, { IGenerateColumns } from "../../Components/ResourceTable";
import { establishments } from "../../Requests";
import PreserveEstablishment from "./PreserveEstablishment";
import RemoveEstablishment from "./RemoveEstablishment";
import ManageEstablishmentUsers from "./ManageEstablishmentUsers";
import useGlobalContext from "../../Hooks/useGlobalContext";

const Establishments: React.FC = () => {
  const { setLoading: setLoadingGlobal } = useGlobalContext();

  const [rechargeRequest, setRechargeRequest] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [idEdit, setIdEdit] = useState<number | null>(null);
  const [idRemove, setIdRemove] = useState(0);

  const [openUsersModal, setOpenUsersModal] = useState(false);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<number | null>(null);

  const reloadResourceTable = () => setRechargeRequest((n) => n + 1);

  const handleDelete = () => (id: number) => {
    setIdRemove(id);
  };

  const handleEdit = () => (id: number) => {
    setLoadingGlobal?.(true);
    setOpenModal(true);
    setIdEdit(id);
    setLoadingGlobal?.(false);
  };

  const handleManageUsers = () => (id: number) => {
    setSelectedEstablishmentId(id);
    setOpenUsersModal(true);
  };

  const handleOpenNew = () => {
    setOpenModal(true);
    setIdEdit(null);
  };

  const generateColumns: IGenerateColumns = ({ setLoading }) => [
    {
      field: "description",
      headerName: "Nome Fantasia",
      minWidth: 250,
      flex: 3
    },
    {
      field: "document",
      headerName: "CNPJ",
      minWidth: 200,
      flex: 2
    },
    {
      field: "corporate_name",
      headerName: "Razão social",
      minWidth: 250,
      flex: 3
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 1,
      valueGetter: (params: any) => (params?.value ?? params) === "active" ? "Ativo" : "Inativo"
    },
    {
      field: "acao_editar",
      headerName: "Editar",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <Tooltip title="Editar estabelecimento">
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => handleEdit()(params.row.id)}
            aria-label="Editar estabelecimento"
          >
            <EditIcon sx={{ color: "#1c2e4a" }} />
          </Box>
        </Tooltip>
      )
    },
    {
      field: "acao_usuarios",
      headerName: "Usuários",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <Tooltip title="Gerenciar usuários do estabelecimento">
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => handleManageUsers()(params.row.id)}
            aria-label="Gerenciar usuários do estabelecimento"
          >
            <GroupIcon sx={{ color: "#1c2e4a" }} />
          </Box>
        </Tooltip>
      )
    },
    {
      field: "acao_remover",
      headerName: "Remover",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <Tooltip title="Remover estabelecimento">
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => handleDelete()(params.row.id)}
            aria-label="Remover estabelecimento"
          >
            <DeleteForeverIcon sx={{ color: "#dc3545" }} />
          </Box>
        </Tooltip>
      )
    }
  ];

  return (
    <Grid container spacing={0.5}>
      <Grid item xs={12} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleIcon />}
          onClick={handleOpenNew}
          aria-label="Adicionar novo estabelecimento"
        >
          Adicionar
        </Button>

        <PreserveEstablishment
          reloadResourceTable={reloadResourceTable}
          openModal={openModal}
          setOpenModal={setOpenModal}
          idEdit={idEdit}
        />
      </Grid>

      <Card
        titleDashboard="Listagem de Estabelecimentos"
        icon="StorefrontIcon"
        paperConfig={{ minHeight: 704 }}
        marginTop="0"
        lineDividerHeader={false}
      >
        <ResourceTable
          getRowId={(row) => row.id ?? row.establishment_alias_id}
          request={establishments}
          generateColumns={generateColumns}
          pageSizeOptions={[15]}
          minHeight={630}
          rechargeRequest={rechargeRequest}
          autoPageSize
          filterDefinitions={({ page, recordsPerPage }) => ({ page, recordsPerPage })}
        />
      </Card>

      {idRemove > 0 && (
        <RemoveEstablishment
          id={idRemove}
          reloadResourceTable={reloadResourceTable}
          close={() => setIdRemove(0)}
        />
      )}

      {openUsersModal && selectedEstablishmentId && (
        <ManageEstablishmentUsers
          establishmentId={selectedEstablishmentId}
          open={openUsersModal}
          close={() => setOpenUsersModal(false)}
        />
      )}
    </Grid>
  );
};

export default Establishments; 